'use client'

import { useEffect, useState, useTransition } from 'react'
import { useRouter } from 'next/navigation'
import { toast } from 'sonner'
import { cancelPendingEmailChange } from './profileActions'

type Props = {
  pendingEmail: string
  currentEmail: string | null
}

/**
 * Client island for PendingEmailBanner. Calm amber (per the "errors inform,
 * they don't shout" design rule) — this is a reminder, not an alarm.
 *
 *   • Dismiss — hides for the rest of the browser session (sessionStorage,
 *     keyed by the pending address so a NEW pending change re-shows). Not
 *     localStorage: "dismiss" shouldn't outlive the session.
 *   • Cancel email change — calls cancelPendingEmailChange (re-points
 *     updateUser at the current email). The authoritative cancel remains the
 *     "this wasn't me" link Supabase mails to the current inbox.
 */
export default function PendingEmailBannerClient({ pendingEmail, currentEmail }: Props) {
  const router = useRouter()
  const [pending, startTransition] = useTransition()
  const [dismissed, setDismissed] = useState(false)
  const storageKey = `sifly:pending-email-dismissed:${pendingEmail}`

  useEffect(() => {
    try {
      if (sessionStorage.getItem(storageKey) === '1') setDismissed(true)
    } catch {
      // sessionStorage unavailable (privacy mode) — just show the banner.
    }
  }, [storageKey])

  function dismiss() {
    try {
      sessionStorage.setItem(storageKey, '1')
    } catch {
      // ignore
    }
    setDismissed(true)
  }

  function cancel() {
    startTransition(async () => {
      const r = await cancelPendingEmailChange()
      if (r.ok) {
        toast.success('Email change cancelled.')
        router.refresh()
      } else {
        toast.error(r.error)
      }
    })
  }

  if (dismissed) return null

  return (
    <div
      role="status"
      aria-label="Pending email change"
      className="border-b border-[var(--color-warning-border)] bg-[var(--color-warning-surface)]"
    >
      <div
        className="mx-auto flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3"
        style={{ maxWidth: 'var(--max-width-page, 72rem)' }}
      >
        <p className="flex-1 text-sm text-[var(--color-warning-strong)]">
          <span className="font-semibold">Confirm your new email.</span>{' '}
          We sent a link to <span className="font-medium">{pendingEmail}</span>
          {currentEmail ? (
            <>
              {' '}and a heads-up to <span className="font-medium">{currentEmail}</span>
            </>
          ) : null}
          . Your sign-in email changes only after you confirm from both inboxes.
        </p>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={cancel}
            disabled={pending}
            className="text-sm font-medium text-[var(--color-warning-strong)] underline hover:no-underline disabled:opacity-60"
          >
            {pending ? 'Cancelling…' : 'Cancel change'}
          </button>
          <button
            type="button"
            onClick={dismiss}
            disabled={pending}
            aria-label="Dismiss for now"
            className="text-sm font-medium text-[var(--color-warning-strong)] hover:opacity-80 disabled:opacity-60"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  )
}
